/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 */

import ai.ChartSpec
import i18n.StringKey
import i18n.Strings
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.dom.events.KeyboardEvent
import org.w3c.fetch.RequestInit
import kotlin.js.json as jsJson

/**
 * Right-rail chat panel — Phase 2c.1 (text streaming only; tools/charts in 2c.2).
 *
 * Owns its own DOM subtree, message state, and streaming machinery. The host
 * component (`WebWiz3RCardDetailComponent`) mounts it via [render] and reacts
 * to [onClose] to fold the panel away.
 */
class WebWiz3RChatPanel(
    private val card: PersonalizedCardData,
    private val onClose: () -> Unit,
    // Invoked when a read-only demo visitor tries to chat — routes them to
    // sign-up. Null outside the demo (a normal session never hits the nudge).
    private val onSignUp: (() -> Unit)? = null,
) {
    private val json = Json { ignoreUnknownKeys = true; encodeDefaults = true }
    private var container: HTMLElement? = null
    private var messagesScroll: HTMLElement? = null
    private var input: HTMLTextAreaElement? = null
    private var sendBtn: HTMLElement? = null
    private var pendingAssistant: HTMLElement? = null
    private var thinkingIndicator: HTMLElement? = null

    private val messages = mutableListOf<ChatMsg>()
    private var isStreaming = false
    private var threadId: String? = null
    private var errorBanner: HTMLElement? = null

    fun render(): HTMLElement {
        val t = WebTheme.current
        val el = document.createElement("div") as HTMLElement
        el.style.cssText = """
            display: flex;
            flex-direction: column;
            background: ${t.card};
            border: 1px solid ${t.border};
            border-radius: 10px;
            box-shadow: ${t.cardShadow};
            height: calc(100vh - 120px);
            min-height: 360px;
            overflow: hidden;
        """.trimIndent()
        container = el

        el.innerHTML = renderHeader(t) +
            """<div id="wiz3r-chat-scroll" style="
                flex: 1;
                overflow-y: auto;
                padding: 12px 14px;
                display: flex;
                flex-direction: column;
                gap: 10px;
            "></div>""" +
            renderInputBar(t)

        messagesScroll = el.querySelector("#wiz3r-chat-scroll") as? HTMLElement
        input = el.querySelector("#wiz3r-chat-input") as? HTMLTextAreaElement
        sendBtn = el.querySelector("#wiz3r-chat-send") as? HTMLElement

        attachHandlers()

        // Empty state visible immediately; history loads async and replaces it.
        renderEmptyPrompt()
        loadHistory()

        return el
    }

    private fun renderHeader(t: theme.ThemeColors): String {
        return """
            <div style="
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 8px;
                padding: 12px 14px;
                border-bottom: 1px solid ${t.border};
            ">
                <div style="display: flex; align-items: center; gap: 8px; min-width: 0;">
                    <div style="
                        width: 26px; height: 26px;
                        border-radius: 6px;
                        background: ${card.iconColor}18;
                        display: flex; align-items: center; justify-content: center;
                        font-size: 14px;
                    ">💬</div>
                    <div style="font-size: 14px; font-weight: 600; color: ${t.textPrimary};">
                        ${escape(StringKey.WIZ3R_CHAT_TITLE.tr())}
                    </div>
                </div>
                <div style="display: flex; gap: 6px;">
                    <button id="wiz3r-chat-clear" title="${escape(StringKey.WIZ3R_CHAT_CLEAR.tr())}"
                        style="background: transparent; border: 1px solid ${t.border}; color: ${t.textSecondary};
                        padding: 4px 8px; border-radius: 6px; cursor: pointer; font-size: 11px;">⌫</button>
                    <button id="wiz3r-chat-close" title="${escape(StringKey.WIZ3R_CHAT_CLOSE.tr())}"
                        style="background: transparent; border: 1px solid ${t.border}; color: ${t.textSecondary};
                        padding: 4px 8px; border-radius: 6px; cursor: pointer; font-size: 11px;">×</button>
                </div>
            </div>
        """.trimIndent()
    }

    private fun renderInputBar(t: theme.ThemeColors): String {
        return """
            <div style="
                display: flex;
                gap: 8px;
                padding: 10px 12px;
                border-top: 1px solid ${t.border};
                background: ${t.card};
            ">
                <textarea
                    id="wiz3r-chat-input"
                    rows="2"
                    placeholder="${escape(StringKey.WIZ3R_CHAT_INPUT_PLACEHOLDER.tr())}"
                    style="
                        flex: 1;
                        resize: none;
                        padding: 8px 10px;
                        border: 1px solid ${t.border};
                        border-radius: 6px;
                        background: ${t.background};
                        color: ${t.textPrimary};
                        font-size: 13px;
                        line-height: 1.4;
                        font-family: inherit;
                    "
                ></textarea>
                <button
                    id="wiz3r-chat-send"
                    style="
                        align-self: flex-end;
                        background: ${card.iconColor};
                        color: white;
                        border: none;
                        padding: 8px 14px;
                        border-radius: 6px;
                        cursor: pointer;
                        font-size: 13px;
                        font-weight: 500;
                    "
                >${escape(StringKey.WIZ3R_CHAT_SEND.tr())}</button>
            </div>
        """.trimIndent()
    }

    private fun renderEmptyPrompt() {
        val t = WebTheme.current
        val scroll = messagesScroll ?: return
        scroll.innerHTML = """
            <div style="
                padding: 14px;
                background: ${card.iconColor}0a;
                border: 1px dashed ${card.iconColor}44;
                border-radius: 8px;
                color: ${t.textSecondary};
                font-size: 12px;
                line-height: 1.5;
            ">
                <div style="font-weight: 600; color: ${t.textPrimary}; margin-bottom: 4px;">
                    ${escape(StringKey.WIZ3R_CHAT_EMPTY_PROMPT_TITLE.tr())}
                </div>
                <div>${escape(StringKey.WIZ3R_CHAT_EMPTY_PROMPT_BODY.tr())}</div>
                <div style="margin-top: 10px; display: flex; flex-direction: column; gap: 4px;">
                    ${suggestion(StringKey.WIZ3R_CHAT_EMPTY_SUGGESTION_1)}
                    ${suggestion(StringKey.WIZ3R_CHAT_EMPTY_SUGGESTION_2)}
                    ${suggestion(StringKey.WIZ3R_CHAT_EMPTY_SUGGESTION_3)}
                </div>
            </div>
        """.trimIndent()
        // Wire suggestion clicks
        val sBtns = scroll.querySelectorAll(".wiz3r-chat-suggestion")
        for (i in 0 until sBtns.length) {
            val b = sBtns.item(i) as? HTMLElement ?: continue
            b.onclick = { _ ->
                val text = b.getAttribute("data-text") ?: ""
                input?.value = text
                input?.focus()
                Unit
            }
        }
    }

    private fun suggestion(key: StringKey): String {
        val t = WebTheme.current
        val text = key.tr()
        return """
            <button class="wiz3r-chat-suggestion" data-text="${escape(text)}"
                style="
                    text-align: left;
                    background: transparent;
                    border: 1px solid ${t.border};
                    color: ${t.textPrimary};
                    border-radius: 6px;
                    padding: 6px 10px;
                    font-size: 12px;
                    cursor: pointer;
                    line-height: 1.3;
                "
            >${escape(text)}</button>
        """.trimIndent()
    }

    private fun attachHandlers() {
        sendBtn?.onclick = { _ ->
            handleSend()
            Unit
        }
        input?.addEventListener("keydown", { event ->
            val ke = event as KeyboardEvent
            // Enter (without shift) sends; Shift+Enter adds a newline.
            if (ke.key == "Enter" && !ke.shiftKey) {
                ke.preventDefault()
                handleSend()
            }
        })
        val closeBtn = container?.querySelector("#wiz3r-chat-close") as? HTMLElement
        closeBtn?.onclick = { _ -> onClose(); Unit }
        val clearBtn = container?.querySelector("#wiz3r-chat-clear") as? HTMLElement
        clearBtn?.onclick = { _ -> handleClear(); Unit }
    }

    private fun handleSend() {
        if (isStreaming) return
        val text = input?.value?.trim().orEmpty()
        if (text.isBlank()) return
        input?.value = ""
        sendMessage(text)
    }

    private fun sendMessage(text: String) {
        // Optimistically render the user message + a "thinking" placeholder.
        // If the prompt area is still showing the empty state, replace it.
        if (messages.isEmpty()) messagesScroll?.innerHTML = ""

        messages.add(ChatMsg(role = "user", content = text))
        appendMessageDom(role = "user", content = text)
        scrollToBottom()

        // The demo token is read-only, so the chat POST would come back 403 and
        // — because the status handler lumps 403 into its catch-all — surface as
        // a bogus "check your connection" error. Short-circuit here: show the
        // sign-up nudge instead of firing a request we know will be refused.
        if (ReadOnlyGate.demoMode) {
            showDemoSignupNudge()
            return
        }

        renderThinking()
        isStreaming = true

        GlobalScope.launch {
            try {
                streamPost(text)
            } catch (e: Throwable) {
                console.warn("Wiz3R chat: stream failed: ${e.message}")
                showError(StringKey.WIZ3R_CHAT_NETWORK_ERROR.tr())
            } finally {
                isStreaming = false
                thinkingIndicator?.remove()
                thinkingIndicator = null
                pendingAssistant = null
            }
        }
    }

    private suspend fun streamPost(userText: String) {
        val token = ApiService.getSharedAuthToken() ?: return showError(StringKey.WIZ3R_CHAT_NETWORK_ERROR.tr())
        val locale = Strings.getLocale().code

        val body = json.encodeToString(
            ChatMessageRequest.serializer(),
            ChatMessageRequest(
                message = userText,
                cardSnapshot = CardSnapshotDto(
                    title = card.title,
                    type = card.type,
                    iconName = card.icon,
                    iconColor = card.iconColor,
                    bodyLines = card.bodyLines,
                    narrative = card.narrative?.let {
                        NarrativeSnapshotDto(it.reveal, it.reason, it.realize)
                    },
                ),
                locale = locale,
            ),
        )

        val baseUrl = getBaseUrl()
        val resp = window.fetch(
            "$baseUrl/api/wiz3r/chat/${encodeURIComponent(card.id)}/message",
            RequestInit(
                method = "POST",
                headers = jsJson(
                    "Authorization" to "Bearer $token",
                    "Content-Type" to "application/json",
                    "Accept" to "text/event-stream",
                    "X-Wiz3r-Tz" to browserTimezone(),
                ),
                body = body,
            ),
        ).await()

        when (resp.status.toInt()) {
            402 -> { showError(StringKey.WIZ3R_CHAT_PRO_REQUIRED.tr()); return }
            403 -> { showDemoSignupNudge(); return } // read-only demo — belt to the demoMode pre-check
            429 -> { showError(StringKey.WIZ3R_CHAT_RATE_LIMITED.tr()); return }
            in 200..299 -> {} // OK
            else -> { showError(StringKey.WIZ3R_CHAT_NETWORK_ERROR.tr()); return }
        }

        val bodyAny: dynamic = resp.asDynamic().body
        val reader = bodyAny.getReader()
        val decoder = js("new TextDecoder('utf-8')")
        val buffer = StringBuilder()
        var assistantContent = StringBuilder()

        while (true) {
            val chunk = (reader.read() as kotlin.js.Promise<dynamic>).await()
            if (chunk.done as Boolean) break
            val text = decoder.decode(chunk.value) as String
            buffer.append(text)

            // SSE frames are separated by \n\n. Pull complete frames out of
            // the buffer; leave the trailing partial frame for the next read.
            while (true) {
                val idx = buffer.indexOf("\n\n")
                if (idx < 0) break
                val frame = buffer.substring(0, idx)
                val remaining = buffer.substring(idx + 2)
                buffer.setLength(0)
                buffer.append(remaining)
                handleFrame(frame, assistantContent)
            }
        }

        // Finalize assistant message in our state.
        val full = assistantContent.toString()
        if (full.isNotBlank()) {
            messages.add(ChatMsg(role = "assistant", content = full))
        }
    }

    private fun handleFrame(frame: String, assistantContent: StringBuilder) {
        var event: String? = null
        var data: String? = null
        for (line in frame.split("\n")) {
            if (line.startsWith("event:")) event = line.removePrefix("event:").trim()
            else if (line.startsWith("data:")) data = line.removePrefix("data:").trim()
        }
        if (event == null || data == null) return
        when (event) {
            "thread" -> {
                val p = try { json.decodeFromString<ThreadIdPayload>(data) } catch (_: Throwable) { return }
                threadId = p.threadId
            }
            "token" -> {
                val p = try { json.decodeFromString<TokenPayload>(data) } catch (_: Throwable) { return }
                thinkingIndicator?.remove()
                thinkingIndicator = null
                if (pendingAssistant == null) pendingAssistant = appendMessageDom(role = "assistant", content = "")
                pendingAssistant?.let { node ->
                    val current = node.getAttribute("data-content") ?: ""
                    val next = current + p.text
                    node.setAttribute("data-content", next)
                    val body = node.querySelector(".wiz3r-chat-msg-body") as? HTMLElement
                    body?.textContent = next
                    assistantContent.append(p.text)
                    scrollToBottom()
                }
            }
            "error" -> {
                val p = try { json.decodeFromString<ErrPayload>(data) } catch (_: Throwable) { return }
                showError(p.message)
            }
            "chart" -> {
                val p = try { json.decodeFromString<ChartPayload>(data) } catch (_: Throwable) { return }
                val spec = try {
                    json.decodeFromString<ChartSpec>(p.chartJson)
                } catch (e: Throwable) {
                    console.warn("Wiz3R chat: failed to decode chart spec: ${e.message}")
                    return
                }
                attachChartToCurrentAssistant(spec)
            }
            "done" -> {
                pendingAssistant = null
                thinkingIndicator?.remove()
                thinkingIndicator = null
            }
        }
    }

    /**
     * Attach a chart SVG to the current in-flight assistant message bubble.
     * If text hasn't started yet, create the bubble first so the chart has a
     * visual home.
     */
    private fun attachChartToCurrentAssistant(spec: ChartSpec) {
        if (pendingAssistant == null) {
            pendingAssistant = appendMessageDom(role = "assistant", content = "")
            thinkingIndicator?.remove()
            thinkingIndicator = null
        }
        val node = pendingAssistant ?: return
        node.appendChild(buildChartContainer(spec))
        scrollToBottom()
    }

    /**
     * Wrap a chart SVG in a clickable container that opens an enlarged modal
     * view on click. Used both for streamed and persisted (history) charts.
     */
    private fun buildChartContainer(spec: ChartSpec): HTMLElement {
        val container = document.createElement("div") as HTMLElement
        container.className = "wiz3r-chat-msg-chart"
        container.style.cssText = """
            margin-top: 10px;
            padding: 6px;
            background: rgba(0,0,0,0.18);
            border-radius: 6px;
            cursor: zoom-in;
            transition: outline 0.15s;
        """.trimIndent()
        container.title = "Click to enlarge"
        container.innerHTML = Wiz3RChart.renderToSvg(spec)
        container.onclick = { _ -> openChartModal(spec); Unit }
        container.addEventListener("mouseover", { container.style.outline = "1px solid ${card.iconColor}66" })
        container.addEventListener("mouseout", { container.style.outline = "none" })
        return container
    }

    /**
     * Full-screen modal overlay showing the chart at large size plus the
     * parameters the LLM picked — transparency so the user can spot a wrong
     * projection themselves.
     */
    private fun openChartModal(spec: ChartSpec) {
        val t = WebTheme.current
        val overlay = document.createElement("div") as HTMLElement
        overlay.style.cssText = """
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.78);
            z-index: 1000;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px;
            cursor: zoom-out;
        """.trimIndent()

        val frame = document.createElement("div") as HTMLElement
        frame.style.cssText = """
            background: ${t.card};
            border: 1px solid ${t.border};
            border-radius: 12px;
            box-shadow: 0 18px 60px rgba(0,0,0,0.6);
            max-width: 1000px;
            width: 100%;
            max-height: calc(100vh - 48px);
            overflow: auto;
            padding: 22px;
            position: relative;
            cursor: default;
        """.trimIndent()

        val closeBtn = document.createElement("button") as HTMLElement
        closeBtn.textContent = "×"
        closeBtn.style.cssText = """
            position: absolute;
            top: 10px;
            right: 12px;
            background: transparent;
            border: 1px solid ${t.border};
            color: ${t.textPrimary};
            font-size: 18px;
            line-height: 1;
            width: 30px;
            height: 30px;
            border-radius: 6px;
            cursor: pointer;
        """.trimIndent()

        val title = document.createElement("h3") as HTMLElement
        title.textContent = spec.title
        title.style.cssText = "margin: 0 30px 8px 0; color: ${t.textPrimary}; font-size: 18px;"

        val chartWrap = document.createElement("div") as HTMLElement
        chartWrap.style.cssText = "margin: 8px 0; padding: 10px; background: rgba(0,0,0,0.18); border-radius: 8px;"
        chartWrap.innerHTML = Wiz3RChart.renderToSvg(spec)
        val svg = chartWrap.querySelector("svg") as? HTMLElement
        svg?.let {
            it.style.width = "100%"
            it.style.height = "auto"
            it.style.maxHeight = "60vh"
        }

        val params = document.createElement("div") as HTMLElement
        params.style.cssText = """
            margin-top: 14px;
            padding: 10px 12px;
            background: rgba(0,0,0,0.10);
            border-radius: 8px;
            font-size: 12px;
            color: ${t.textSecondary};
            font-family: monospace;
            line-height: 1.6;
        """.trimIndent()
        params.innerHTML = renderChartMetadata(spec)

        frame.appendChild(closeBtn)
        frame.appendChild(title)
        frame.appendChild(chartWrap)
        frame.appendChild(params)
        overlay.appendChild(frame)
        document.body?.appendChild(overlay)

        val close: (org.w3c.dom.events.Event?) -> Unit = { _ ->
            overlay.parentNode?.removeChild(overlay)
            Unit
        }
        overlay.onclick = { event ->
            // Clicks on the frame shouldn't close — only the dim background.
            if (event.target == overlay) close(event)
        }
        closeBtn.onclick = { event -> event.stopPropagation(); close(event) }
        // Esc to close
        val escHandler = object {
            val fn: (org.w3c.dom.events.Event) -> Unit = { ev ->
                val ke = ev as? org.w3c.dom.events.KeyboardEvent
                if (ke != null && ke.key == "Escape") {
                    document.removeEventListener("keydown", this.fn)
                    close(ev)
                }
            }
        }
        document.addEventListener("keydown", escHandler.fn)
    }

    /**
     * Render the chart's metadata (axis labels, percentile bands, source
     * parameters where derivable) as plain text. Helps the user judge whether
     * the LLM picked sensible inputs.
     */
    private fun renderChartMetadata(spec: ChartSpec): String {
        val lines = mutableListOf<String>()
        spec.xAxisLabel?.let { lines += "X-axis: ${escape(it)}" }
        spec.yAxisLabel?.let { lines += "Y-axis: ${escape(it)}" }
        when (spec) {
            is ai.MonteCarloFan -> {
                lines += "Horizon: ${spec.years.size - 1} years (${spec.years.first()} → ${spec.years.last()})"
                spec.band25_75?.let { lines += "Shaded bands: 25–75% (inner) and 10–90% (outer) of 5000 Monte Carlo scenarios" }
                if (spec.median.isNotEmpty()) {
                    val end = spec.median.last()
                    lines += "Median final value: \$${formatNumber(end)}"
                }
            }
            else -> {}
        }
        return lines.joinToString("<br>")
    }

    private fun formatNumber(d: Double): String {
        val n = kotlin.math.abs(d).toLong()
        val s = n.toString().reversed().chunked(3).joinToString(",").reversed()
        return if (d < 0) "-$s" else s
    }

    private fun appendMessageDom(role: String, content: String): HTMLElement {
        val t = WebTheme.current
        val isUser = role == "user"
        val node = document.createElement("div") as HTMLElement
        node.className = "wiz3r-chat-msg wiz3r-chat-msg-$role"
        node.setAttribute("data-content", content)
        val bgColor = if (isUser) "${card.iconColor}1f" else "${t.background}"
        val borderColor = if (isUser) "${card.iconColor}55" else t.border
        node.style.cssText = """
            align-self: ${if (isUser) "flex-end" else "flex-start"};
            max-width: 90%;
            padding: 8px 12px;
            border-radius: 10px;
            background: $bgColor;
            border: 1px solid $borderColor;
            color: ${t.textPrimary};
            font-size: 13px;
            line-height: 1.5;
            white-space: pre-wrap;
            word-wrap: break-word;
        """.trimIndent()
        val body = document.createElement("div") as HTMLElement
        body.className = "wiz3r-chat-msg-body"
        body.textContent = content
        node.appendChild(body)
        messagesScroll?.appendChild(node)
        return node
    }

    private fun renderThinking() {
        val t = WebTheme.current
        val node = document.createElement("div") as HTMLElement
        node.style.cssText = """
            align-self: flex-start;
            padding: 8px 12px;
            border-radius: 10px;
            background: ${t.background};
            border: 1px solid ${t.border};
            color: ${t.textSecondary};
            font-size: 12px;
            font-style: italic;
        """.trimIndent()
        node.textContent = StringKey.WIZ3R_CHAT_THINKING.tr()
        messagesScroll?.appendChild(node)
        thinkingIndicator = node
    }

    /**
     * Read-only demo hit the chat: instead of an error, a friendly nudge with a
     * sign-up call to action. Reads as a reason to create an account, not as a
     * broken feature — which matters most on the demo, our shop window.
     */
    private fun showDemoSignupNudge() {
        val t = WebTheme.current
        errorBanner?.remove()
        val node = document.createElement("div") as HTMLElement
        node.style.cssText = """
            align-self: stretch;
            margin: 4px 0;
            padding: 12px 14px;
            border-radius: 8px;
            background: ${t.accent}12;
            border: 1px solid ${t.accent}55;
            color: ${t.textPrimary};
            font-size: 13px;
            display: flex;
            flex-direction: column;
            gap: 10px;
        """.trimIndent()

        val text = document.createElement("span") as HTMLElement
        text.textContent = StringKey.WIZ3R_CHAT_DEMO_SIGNUP.tr()
        node.appendChild(text)

        onSignUp?.let { signUp ->
            val btn = document.createElement("button") as HTMLElement
            btn.textContent = StringKey.AUTH_CREATE_ACCOUNT.tr()
            btn.style.cssText = """
                align-self: flex-start;
                padding: 7px 14px;
                border: none;
                border-radius: 6px;
                background: ${t.accent};
                color: #0a0a0a;
                font-size: 13px;
                font-weight: 600;
                cursor: pointer;
            """.trimIndent()
            btn.onclick = { signUp() }
            node.appendChild(btn)
        }

        messagesScroll?.appendChild(node)
        errorBanner = node
        scrollToBottom()
    }

    private fun showError(msg: String) {
        val t = WebTheme.current
        errorBanner?.remove()
        val node = document.createElement("div") as HTMLElement
        node.style.cssText = """
            align-self: stretch;
            margin: 4px 0;
            padding: 8px 12px;
            border-radius: 8px;
            background: #b3261e15;
            border: 1px solid #b3261e55;
            color: #ff8a80;
            font-size: 12px;
        """.trimIndent()
        node.textContent = msg
        messagesScroll?.appendChild(node)
        errorBanner = node
        scrollToBottom()
    }

    private fun scrollToBottom() {
        val s = messagesScroll ?: return
        s.scrollTop = s.scrollHeight.toDouble()
    }

    private fun loadHistory() {
        GlobalScope.launch {
            try {
                val token = ApiService.getSharedAuthToken() ?: return@launch
                val baseUrl = getBaseUrl()
                val resp = window.fetch(
                    "$baseUrl/api/wiz3r/chat/${encodeURIComponent(card.id)}/history",
                    RequestInit(
                        method = "GET",
                        headers = jsJson("Authorization" to "Bearer $token"),
                    ),
                ).await()
                if (!resp.ok) return@launch
                val text = resp.text().await()
                val parsed = json.decodeFromString<ChatHistoryResponse>(text)
                threadId = parsed.threadId
                if (parsed.messages.isEmpty()) return@launch
                messagesScroll?.innerHTML = ""
                messages.clear()
                for (m in parsed.messages) {
                    messages.add(ChatMsg(role = m.role, content = m.content))
                    val node = appendMessageDom(role = m.role, content = m.content)
                    // Render any persisted chart attached to this message.
                    m.chartJson?.let { rawChartJson ->
                        try {
                            val spec = json.decodeFromString<ChartSpec>(rawChartJson)
                            node.appendChild(buildChartContainer(spec))
                        } catch (e: Throwable) {
                            console.warn("Wiz3R chat: persisted chart decode failed: ${e.message}")
                        }
                    }
                }
                scrollToBottom()
            } catch (e: Throwable) {
                console.warn("Wiz3R chat: failed to load history: ${e.message}")
            }
        }
    }

    private fun handleClear() {
        showAppConfirm(
            title = StringKey.WIZ3R_CHAT_CLEAR_CONFIRM.tr(),
            confirmLabel = StringKey.ACTION_CLEAR.tr(),
            destructive = true,
        ) { handleClearConfirmed() }
    }

    private fun handleClearConfirmed() {
        GlobalScope.launch {
            try {
                val token = ApiService.getSharedAuthToken() ?: return@launch
                val baseUrl = getBaseUrl()
                window.fetch(
                    "$baseUrl/api/wiz3r/chat/${encodeURIComponent(card.id)}",
                    RequestInit(
                        method = "DELETE",
                        headers = jsJson("Authorization" to "Bearer $token"),
                    ),
                ).await()
                messages.clear()
                threadId = null
                renderEmptyPrompt()
            } catch (e: Throwable) {
                console.warn("Wiz3R chat: delete failed: ${e.message}")
            }
        }
    }

    private fun getBaseUrl(): String {
        val hostname = window.location.hostname
        return if (hostname == "localhost" || hostname == "127.0.0.1") "http://localhost:3000" else ""
    }

    private fun encodeURIComponent(s: String): String = js("encodeURIComponent")(s) as String

    private fun escape(s: String): String =
        HtmlEscape.escape(s)

    private data class ChatMsg(val role: String, val content: String)
}

// Wire format mirrors the server's payloads
@Serializable
private data class ChatMessageRequest(
    val message: String,
    val cardSnapshot: CardSnapshotDto,
    val locale: String? = null,
)

@Serializable
private data class CardSnapshotDto(
    val title: String,
    val type: String,
    val iconName: String? = null,
    val iconColor: String? = null,
    val bodyLines: List<String> = emptyList(),
    val narrative: NarrativeSnapshotDto? = null,
)

@Serializable
private data class NarrativeSnapshotDto(
    val reveal: String,
    val reason: String,
    val realize: String,
)

@Serializable
private data class ChatHistoryResponse(
    val threadId: String,
    val messages: List<ChatHistoryMessageDto>,
)

@Serializable
private data class ChatHistoryMessageDto(
    val id: Long,
    val role: String,
    val content: String,
    val chartJson: String? = null,
    val createdAt: String,
)

@Serializable
private data class ThreadIdPayload(val threadId: String)

@Serializable
private data class TokenPayload(val text: String)

@Serializable
private data class ErrPayload(val message: String)

@Serializable
private data class ChartPayload(val chartJson: String)
